/**
 * Construction de la requête Overpass QL et découpage en tronçons —
 * EF-POI-009, EF-POI-002, EF-POI-003.
 */

import { categoryDefinition, type CategoryKey, type CategoryMatch } from '../core/categories';
import type { SimplifiedPoint } from '../geo/simplify';
import { SIMPLIFY_TOLERANCE_METERS } from '../geo/simplify';

/** Délai déclaré au serveur, égal au délai d'attente client. */
export const OVERPASS_TIMEOUT_SECONDS = 60;

/** Longueur de progression maximale d'un tronçon, en mètres. */
export const MAX_CHUNK_PROGRESSION_METERS = 50_000;

/** Nombre maximal de points simplifiés dans un tronçon. */
export const MAX_CHUNK_POINTS = 200;

/** Recouvrement entre deux tronçons consécutifs, en mètres. */
export const CHUNK_OVERLAP_METERS = 1000;

/** Durée unitaire de référence d'un tronçon, en secondes. */
export const CHUNK_REFERENCE_SECONDS = 8;

/** Surface de corridor au-delà de laquelle une confirmation est exigée, en m². */
export const AREA_ALERT_SQUARE_METERS = 5000 * 1_000_000;

/** Rayon demandé à Overpass : corridor augmenté de la tolérance de simplification. */
export function requestRadiusMeters(corridorMeters: number): number {
  return corridorMeters + SIMPLIFY_TOLERANCE_METERS;
}

/** Une condition à valeur unique donne `="v"`, plusieurs valeurs une regex ancrée. */
function renderMatch(match: CategoryMatch): string {
  return match.conditions
    .map((condition) => {
      const first = condition.values[0];
      if (condition.values.length === 1 && first !== undefined) {
        return `["${condition.key}"="${first}"]`;
      }
      return `["${condition.key}"~"^(${condition.values.join('|')})$"]`;
    })
    .join('');
}

function renderCoordinates(points: readonly SimplifiedPoint[]): string {
  return points
    .map((point) => `${point.coordinate.lat.toFixed(7)},${point.coordinate.lon.toFixed(7)}`)
    .join(',');
}

/**
 * Corps de la requête : une union de sélections suivie d'une sortie unique.
 *
 * Les filtres d'exclusion de EF-POI-013 ne sont **jamais** poussés dans la
 * requête : les exprimer côté serveur rendrait impossible le décompte des
 * objets écartés et fausserait le nombre d'objets OSM reçus.
 */
export function buildQuery(
  categories: readonly CategoryKey[],
  points: readonly SimplifiedPoint[],
  radiusMeters: number,
): string {
  const coordinates = renderCoordinates(points);
  const radius = Math.round(radiusMeters);
  const selections: string[] = [];

  for (const key of categories) {
    const definition = categoryDefinition(key);
    if (!definition.searchable) continue;
    for (const match of definition.matches) {
      selections.push(`  nwr(around:${radius},${coordinates})${renderMatch(match)};`);
    }
  }

  return [
    `[out:json][timeout:${OVERPASS_TIMEOUT_SECONDS}];`,
    '(',
    ...selections,
    ');',
    'out tags center;',
  ].join('\n');
}

export interface SearchChunk {
  /** Rang du tronçon, à partir de 0. */
  readonly index: number;
  readonly points: readonly SimplifiedPoint[];
  readonly fromProgressionMeters: number;
  readonly toProgressionMeters: number;
}

/**
 * Découpe les points simplifiés de la portion recherchée en tronçons contigus.
 * Un tronçon couvre au plus 50 km de progression et au plus 200 points, la
 * première limite atteinte fermant le tronçon. Deux tronçons consécutifs se
 * recouvrent d'un tronçon de 1 km, absorbé par la déduplication EF-POI-010.
 */
export function buildChunks(
  simplified: readonly SimplifiedPoint[],
  fromProgressionMeters: number,
  toProgressionMeters: number,
): readonly SearchChunk[] {
  const inRange = simplified.filter(
    (point) =>
      point.progressionMeters >= fromProgressionMeters && point.progressionMeters <= toProgressionMeters,
  );
  if (inRange.length < 2) return [];

  const chunks: SearchChunk[] = [];
  let start = 0;

  while (start < inRange.length - 1) {
    const startPoint = inRange[start];
    if (startPoint === undefined) break;

    let end = start;
    while (end + 1 < inRange.length) {
      const next = inRange[end + 1];
      if (next === undefined) break;
      // Un tronçon est une polyligne : il compte toujours au moins deux points.
      // Sur une portion rectiligne de plus de 50 km, la simplification ne
      // fournit aucun point intermédiaire et la borne ne peut être tenue.
      if (end > start) {
        if (next.progressionMeters - startPoint.progressionMeters > MAX_CHUNK_PROGRESSION_METERS) break;
        if (end + 2 - start > MAX_CHUNK_POINTS) break;
      }
      end += 1;
    }

    const points = inRange.slice(start, end + 1);
    const first = points[0];
    const last = points[points.length - 1];
    if (first === undefined || last === undefined) break;

    chunks.push({
      index: chunks.length,
      points,
      fromProgressionMeters: first.progressionMeters,
      toProgressionMeters: last.progressionMeters,
    });

    if (end >= inRange.length - 1) break;

    // Recul d'un kilomètre : le tronçon suivant démarre au dernier point situé
    // au plus à `last − 1 km`, de sorte que le recouvrement soit effectif.
    const overlapFrom = last.progressionMeters - CHUNK_OVERLAP_METERS;
    let next = end - 1;
    while (next > start) {
      const candidate = inRange[next];
      if (candidate === undefined) break;
      if (candidate.progressionMeters <= overlapFrom) break;
      next -= 1;
    }
    start = Math.max(next, start + 1);
  }

  return chunks;
}

export interface SearchEstimate {
  readonly chunkCount: number;
  readonly interrogatedLengthMeters: number;
  readonly corridorAreaSquareMeters: number;
  readonly estimatedSeconds: number;
  readonly categories: readonly CategoryKey[];
  readonly radiusMeters: number;
  /** Vrai lorsque la durée estimée dépasse le budget de l'utilisateur. */
  readonly exceedsBudget: boolean;
  /** Vrai lorsque la surface interrogée dépasse 5 000 km². */
  readonly exceedsArea: boolean;
}

/**
 * Estimation préalable, présentée **sans aucun appel réseau** (EF-POI-009,
 * ENF-015).
 */
export function estimateSearch(input: {
  readonly chunks: readonly SearchChunk[];
  readonly categories: readonly CategoryKey[];
  readonly corridorMeters: number;
  readonly fromProgressionMeters: number;
  readonly toProgressionMeters: number;
  readonly budgetSeconds: number;
}): SearchEstimate {
  const radiusMeters = requestRadiusMeters(input.corridorMeters);
  const interrogatedLengthMeters = Math.max(0, input.toProgressionMeters - input.fromProgressionMeters);
  const corridorAreaSquareMeters = interrogatedLengthMeters * 2 * radiusMeters;
  const estimatedSeconds = input.chunks.length * CHUNK_REFERENCE_SECONDS;

  return {
    chunkCount: input.chunks.length,
    interrogatedLengthMeters,
    corridorAreaSquareMeters,
    estimatedSeconds,
    categories: input.categories,
    radiusMeters,
    exceedsBudget: estimatedSeconds > input.budgetSeconds,
    exceedsArea: corridorAreaSquareMeters > AREA_ALERT_SQUARE_METERS,
  };
}
